import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function Solution() {
  const { t } = useTranslation();
  
  const solutions = [
    {
      icon: 'ri-robot-2-fill',
      title: t('solution.aiAgents.title'),
      description: t('solution.aiAgents.description'),
      features: [
        t('solution.aiAgents.features.context'),
        t('solution.aiAgents.features.natural'),
        t('solution.aiAgents.features.learning')
      ],
      color: 'purple',
      type: 'ai-chat'
    },
    {
      icon: 'ri-mic-fill',
      title: t('solution.voiceAgents.title'),
      description: t('solution.voiceAgents.description'),
      features: [
        t('solution.voiceAgents.features.humanized'),
        t('solution.voiceAgents.features.instant'),
        t('solution.voiceAgents.features.multilingual')
      ],
      color: 'green',
      type: 'voice-chat'
    },
    {
      icon: 'ri-dashboard-fill',
      title: t('solution.crm.title'),
      description: t('solution.crm.description'),
      features: [
        t('solution.crm.features.pipeline'),
        t('solution.crm.features.automation'),
        t('solution.crm.features.analytics')
      ],
      color: 'blue',
      type: 'crm-kanban'
    },
    {
      icon: 'ri-team-fill',
      title: t('solution.inbox.title'),
      description: t('solution.inbox.description'),
      features: [
        t('solution.inbox.features.distribution'),
        t('solution.inbox.features.handoff'),
        t('solution.inbox.features.monitoring')
      ],
      color: 'pink',
      type: 'multi-inbox'
    }
  ];

  const colorClasses = {
    purple: {
      gradient: 'from-purple-600 to-purple-500',
      border: 'border-purple-500/50',
      bg: 'bg-purple-500/10',
      text: 'text-purple-400'
    },
    green: {
      gradient: 'from-green-600 to-green-500',
      border: 'border-green-500/50',
      bg: 'bg-green-500/10',
      text: 'text-green-400'
    },
    blue: {
      gradient: 'from-blue-600 to-blue-500',
      border: 'border-blue-500/50',
      bg: 'bg-blue-500/10',
      text: 'text-blue-400'
    },
    pink: {
      gradient: 'from-pink-600 to-pink-500',
      border: 'border-pink-500/50',
      bg: 'bg-pink-500/10',
      text: 'text-pink-400'
    }
  };

  return (
    <section 
      id="solution"
      className="relative py-24 px-6 overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #0a0e14 0%, #1b2433 25%, #1b2433 75%, #0a0e14 100%)'
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-tr from-purple-900/5 via-green-900/5 to-blue-900/5"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500/10 to-green-500/10 border border-purple-500/30 rounded-full mb-6">
            <i className="ri-lightbulb-flash-fill text-green-400"></i>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-green-300 text-sm font-medium">{t('solution.badge')}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('solution.title')}<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-green-400">{t('solution.titleHighlight')}</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            {t('solution.subtitle')}
          </p>
        </div>

        <div className="space-y-16">
          {solutions.map((solution, index) => (
            <div 
              key={index}
              className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center`}
            >
              <div className="flex-1">
                <div className={`inline-flex items-center gap-2 px-4 py-2 ${colorClasses[solution.color as keyof typeof colorClasses].bg} border ${colorClasses[solution.color as keyof typeof colorClasses].border} rounded-full mb-6`}>
                  <i className={`${solution.icon} ${colorClasses[solution.color as keyof typeof colorClasses].text}`}></i>
                  <span className={`${colorClasses[solution.color as keyof typeof colorClasses].text} text-sm font-medium`}>{solution.title}</span>
                </div>

                <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">{solution.title}</h3>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed">{solution.description}</p>

                <div className="space-y-4">
                  {solution.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className={`w-6 h-6 flex items-center justify-center rounded-full bg-gradient-to-br ${colorClasses[solution.color as keyof typeof colorClasses].gradient}`}>
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex-1">
                {solution.type === 'ai-chat' ? (
                  <AIChatMockup color={solution.color} />
                ) : solution.type === 'voice-chat' ? (
                  <VoiceChatMockup color={solution.color} />
                ) : solution.type === 'crm-kanban' ? (
                  <CRMKanbanMockup color={solution.color} />
                ) : solution.type === 'multi-inbox' ? (
                  <MultiInboxMockup color={solution.color} />
                ) : (
                  <div className="relative group cursor-pointer">
                    <div className={`absolute inset-0 bg-gradient-to-br ${colorClasses[solution.color as keyof typeof colorClasses].gradient} rounded-2xl blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-300`}></div>
                    <div className="relative bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl overflow-hidden">
                      <img 
                        src={solution.image}
                        alt={solution.title}
                        className="w-full h-80 object-cover object-top"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AIChatMockup({ color }: { color: string }) {
  const { t } = useTranslation();
  const [visibleMessages, setVisibleMessages] = useState<number[]>([]);
  const [showTyping, setShowTyping] = useState(false);

  const messages = [
    { type: 'user', text: t('solution.chat.user1') },
    { type: 'bot', text: t('solution.chat.bot1') },
    { type: 'user', text: t('solution.chat.user2') },
    { type: 'bot', text: t('solution.chat.bot2') }
  ];

  useEffect(() => {
    const showMessage = (index: number) => {
      if (index >= messages.length) {
        setTimeout(() => {
          setVisibleMessages([]);
          showMessage(0);
        }, 3000);
        return;
      }

      if (messages[index].type === 'bot') {
        setShowTyping(true);
        setTimeout(() => {
          setShowTyping(false);
          setVisibleMessages(prev => [...prev, index]);
          setTimeout(() => showMessage(index + 1), 1500);
        }, 1000);
      } else {
        setVisibleMessages(prev => [...prev, index]);
        setTimeout(() => showMessage(index + 1), 1200);
      }
    };

    showMessage(0);
  }, []);

  return (
    <div className="relative group cursor-pointer">
      <div className="absolute inset-0 bg-white/5 rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
      <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
        {/* WhatsApp Header */}
        <div className="bg-[#075E54] px-4 py-3 flex items-center gap-3">
          <img
            src="/assets/media/agent-julia.png"
            alt={t('solution.chat.assistant')}
            className="w-10 h-10 rounded-full object-cover object-top"
          />
          <div className="flex-1">
            <div className="text-white font-semibold text-sm">{t('solution.chat.assistant')}</div>
            <div className="text-green-200 text-xs">{t('solution.chat.online')}</div>
          </div>
          <i className="ri-more-2-fill text-white text-xl cursor-pointer"></i>
        </div>

        {/* Chat Background */}
        <div className="bg-[#0D1418] p-4 h-80 overflow-y-auto">
          <div className="space-y-3">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`${
                  visibleMessages.includes(index) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                } transition-all duration-500 ease-out ${
                  message.type === 'user' ? 'flex justify-end' : 'flex gap-2'
                }`}
              >
                {message.type === 'bot' && (
                  <img
                    src="/assets/media/agent-julia.png"
                    alt={t('solution.chat.assistant')}
                    className="w-8 h-8 rounded-full object-cover object-top flex-shrink-0 mt-auto"
                  />
                )}
                <div
                  className={`${
                    message.type === 'user'
                      ? 'bg-[#005C4B] text-white rounded-lg rounded-tr-sm'
                      : 'bg-[#1F2C34] text-white rounded-lg rounded-tl-sm'
                  } px-4 py-2 max-w-xs shadow-lg`}
                >
                  <p className="text-sm leading-relaxed">{message.text}</p>
                  <div className="text-xs text-gray-400 mt-1 text-right">
                    {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ))}

            {/* Typing Indicator */}
            {showTyping && (
              <div className="flex gap-2 opacity-100 transition-opacity duration-300">
                <img
                  src="/assets/media/agent-julia.png"
                  alt={t('solution.chat.assistant')}
                  className="w-8 h-8 rounded-full object-cover object-top flex-shrink-0"
                />
                <div className="bg-[#1F2C34] px-4 py-3 rounded-lg rounded-tl-sm">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Input Area */}
        <div className="bg-[#1F2C34] px-4 py-3 flex items-center gap-3">
          <i className="ri-emotion-happy-line text-gray-400 text-xl cursor-pointer"></i>
          <div className="flex-1 bg-[#2A3942] rounded-full px-4 py-2">
            <span className="text-gray-500 text-sm">{t('solution.chat.typing')}</span>
          </div>
          <i className="ri-mic-fill text-gray-400 text-xl cursor-pointer"></i>
        </div>
      </div>
    </div>
  );
}

function VoiceChatMockup({ color }: { color: string }) {
  const { t } = useTranslation();
  const [visibleMessages, setVisibleMessages] = useState<number[]>([]);
  const [showTyping, setShowTyping] = useState(false);
  const [isRecording, setIsRecording] = useState(false);

  const messages = [
    { type: 'user', text: '🎤 Áudio (0:05)', isVoice: true },
    { type: 'bot', text: '🎤 Áudio (0:08)', isVoice: true },
    { type: 'user', text: '🎤 Áudio (0:03)', isVoice: true },
    { type: 'bot', text: '🎤 Áudio (0:06)', isVoice: true }
  ];

  useEffect(() => {
    const showMessage = (index: number) => {
      if (index >= messages.length) {
        setTimeout(() => {
          setVisibleMessages([]);
          setIsRecording(false);
          showMessage(0);
        }, 3000);
        return;
      }

      if (messages[index].isVoice) {
        setIsRecording(true);
        setTimeout(() => {
          setIsRecording(false);
          setVisibleMessages(prev => [...prev, index]);
          setTimeout(() => showMessage(index + 1), 1000);
        }, 1500);
      } else if (messages[index].type === 'bot') {
        setShowTyping(true);
        setTimeout(() => {
          setShowTyping(false);
          setVisibleMessages(prev => [...prev, index]);
          setTimeout(() => showMessage(index + 1), 1500);
        }, 1000);
      } else {
        setVisibleMessages(prev => [...prev, index]);
        setTimeout(() => showMessage(index + 1), 1200);
      }
    };

    showMessage(0);
  }, []);

  return (
    <div className="relative group cursor-pointer">
      {/* Outer Glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-green-500/30 via-emerald-500/20 to-teal-500/30 rounded-2xl blur-2xl opacity-40 group-hover:opacity-60 transition-opacity duration-300"></div>
      
      {/* Main Container with Liquid Glass Effect */}
      <div className="relative backdrop-blur-2xl bg-gradient-to-br from-green-500/10 via-emerald-500/5 to-teal-500/10 border-2 border-white/20 rounded-2xl overflow-hidden shadow-2xl">
        {/* Glossy Reflection Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent pointer-events-none"></div>
        
        {/* WhatsApp Header with Glass Effect */}
        <div className="relative backdrop-blur-xl bg-gradient-to-r from-green-600/80 via-green-500/70 to-emerald-600/80 px-4 py-3 flex items-center gap-3 border-b border-white/10">
          <div className="w-10 h-10 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full border-2 border-white/30 shadow-lg">
            <i className="ri-mic-fill text-white text-lg drop-shadow-lg"></i>
          </div>
          <div className="flex-1">
            <div className="text-white font-semibold text-sm drop-shadow-md">{t('solution.voice.assistant')}</div>
            <div className="text-green-100 text-xs flex items-center gap-1">
              <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse shadow-lg shadow-green-400/50"></div>
              {t('solution.voice.recording')}
            </div>
          </div>
          <i className="ri-more-2-fill text-white text-xl cursor-pointer drop-shadow-md"></i>
        </div>

        {/* Chat Background with Enhanced Glass */}
        <div className="relative backdrop-blur-xl bg-gradient-to-br from-gray-900/40 via-gray-800/30 to-gray-900/40 p-4 h-80 overflow-y-auto">
          {/* Subtle Pattern Overlay */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.03),transparent_50%)] pointer-events-none"></div>
          
          <div className="space-y-3 relative z-10">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`${
                  visibleMessages.includes(index) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                } transition-all duration-500 ease-out ${
                  message.type === 'user' ? 'flex justify-end' : 'flex gap-2'
                }`}
              >
                {message.type === 'bot' && (
                  <div className="w-8 h-8 flex items-center justify-center bg-white/20 backdrop-blur-md rounded-full border-2 border-white/30 flex-shrink-0 mt-auto shadow-lg">
                    <i className="ri-mic-fill text-white text-sm drop-shadow-md"></i>
                  </div>
                )}
                <div
                  className={`${
                    message.type === 'user'
                      ? 'backdrop-blur-xl bg-green-600/60 border border-white/20 text-white rounded-lg rounded-tr-sm shadow-lg'
                      : 'backdrop-blur-xl bg-gray-800/60 border border-white/10 text-white rounded-lg rounded-tl-sm shadow-lg'
                  } px-4 py-2 max-w-xs`}
                >
                  {message.isVoice ? (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-white/30 backdrop-blur-sm rounded-full border border-white/40 shadow-md">
                        <i className="ri-play-fill text-white text-sm drop-shadow-md"></i>
                      </div>
                      <div className="flex-1 flex items-center gap-1">
                        {[...Array(20)].map((_, i) => (
                          <div
                            key={i}
                            className="w-1 bg-white/80 rounded-full shadow-sm"
                            style={{ height: `${Math.random() * 20 + 10}px` }}
                          ></div>
                        ))}
                      </div>
                      <span className="text-xs text-white/90 font-medium drop-shadow-sm">{message.text.match(/\d+:\d+/)?.[0]}</span>
                    </div>
                  ) : (
                    <p className="text-sm leading-relaxed drop-shadow-sm">{message.text}</p>
                  )}
                  <div className="text-xs text-white/70 mt-1 text-right drop-shadow-sm">
                    {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ))}

            {/* Typing Indicator */}
            {showTyping && (
              <div className="flex gap-2 opacity-100 transition-opacity duration-300">
                <div className="w-8 h-8 flex items-center justify-center bg-white/20 backdrop-blur-md rounded-full border-2 border-white/30 flex-shrink-0 shadow-lg">
                  <i className="ri-mic-fill text-white text-sm drop-shadow-md"></i>
                </div>
                <div className="backdrop-blur-xl bg-gray-800/60 border border-white/10 px-4 py-3 rounded-lg rounded-tl-sm shadow-lg">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-white/80 rounded-full animate-bounce shadow-sm" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-white/80 rounded-full animate-bounce shadow-sm" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-white/80 rounded-full animate-bounce shadow-sm" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Voice Input Area with Glass Effect */}
        <div className="relative backdrop-blur-xl bg-gradient-to-r from-gray-800/60 via-gray-700/50 to-gray-800/60 border-t border-white/10 px-4 py-3 flex items-center justify-center gap-4">
          <div className={`w-16 h-16 flex items-center justify-center rounded-full ${
            isRecording 
              ? 'bg-gradient-to-br from-red-500/80 to-red-600/80 backdrop-blur-md border-2 border-red-300/50 animate-pulse shadow-xl shadow-red-500/50' 
              : 'bg-white/20 backdrop-blur-md border-2 border-white/30 shadow-lg'
          } cursor-pointer transition-all duration-300`}>
            <i className={`${isRecording ? 'ri-stop-fill' : 'ri-mic-fill'} text-white text-2xl drop-shadow-lg`}></i>
          </div>
          {isRecording && (
            <div className="flex items-center gap-1">
              {[...Array(30)].map((_, i) => (
                <div
                  key={i}
                  className="w-1 bg-white/80 rounded-full animate-pulse shadow-sm"
                  style={{ 
                    height: `${Math.random() * 30 + 10}px`,
                    animationDelay: `${i * 50}ms`
                  }}
                ></div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CRMKanbanMockup({ color }: { color: string }) {
  return (
    <div className="relative group cursor-pointer">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-500 rounded-2xl blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-300"></div>
      <div className="relative bg-[#0f1729] backdrop-blur-sm border border-gray-800 rounded-2xl overflow-hidden">
        {/* CRM Header */}
        <div className="bg-[#1a2332] px-3 py-2 flex items-center justify-between border-b border-gray-800">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center bg-blue-500 rounded-lg">
              <i className="ri-layout-board-fill text-white text-sm"></i>
            </div>
            <span className="text-white font-semibold text-sm">Kanban Board</span>
          </div>
          <div className="flex items-center gap-2">
            <button className="px-2 py-1 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs rounded-lg transition-colors whitespace-nowrap">
              <i className="ri-filter-3-line"></i>
            </button>
            <div className="flex items-center -space-x-2">
              <img src="/assets/media/agent-julia.png" alt="User 1" className="w-6 h-6 rounded-full border-2 border-[#1a2332] object-cover object-top" />
              <div className="w-6 h-6 rounded-full border-2 border-[#1a2332] bg-purple-500 flex items-center justify-center text-white text-xs font-semibold">M</div>
              <div className="w-6 h-6 rounded-full border-2 border-[#1a2332] bg-green-500 flex items-center justify-center text-white text-xs font-semibold">R</div>
            </div>
          </div>
        </div>

        {/* Metrics Bar */}
        <div className="bg-[#0f1729] px-3 py-2 grid grid-cols-3 gap-2 border-b border-gray-800">
          <div className="bg-[#1a2332] rounded-lg p-2">
            <div className="flex items-center gap-1 mb-1">
              <i className="ri-team-fill text-purple-400 text-xs"></i>
              <span className="text-gray-400 text-xs">Leads</span>
            </div>
            <div className="text-white font-bold text-sm">10</div>
          </div>
          <div className="bg-[#1a2332] rounded-lg p-2">
            <div className="flex items-center gap-1 mb-1">
              <i className="ri-money-dollar-circle-fill text-green-400 text-xs"></i>
              <span className="text-gray-400 text-xs">Valor</span>
            </div>
            <div className="text-white font-bold text-sm">R$ 23k</div>
          </div>
          <div className="bg-[#1a2332] rounded-lg p-2">
            <div className="flex items-center gap-1 mb-1">
              <i className="ri-line-chart-fill text-blue-400 text-xs"></i>
              <span className="text-gray-400 text-xs">Taxa</span>
            </div>
            <div className="text-white font-bold text-sm">30%</div>
          </div>
        </div>

        {/* Kanban Columns */}
        <div className="bg-[#0f1729] p-3 h-64 overflow-x-auto">
          <div className="flex gap-2 min-w-max">
            {/* Column 1: Pré-Qualificados */}
            <div className="w-48 bg-[#1a2332] rounded-lg border border-gray-800">
              <div className="p-2 border-b border-gray-800">
                <div className="flex items-center gap-1 mb-1">
                  <i className="ri-robot-fill text-purple-400 text-xs"></i>
                  <span className="text-white font-semibold text-xs">Pré-Qualificados</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="bg-red-500/20 text-red-400 px-1 py-0.5 rounded text-xs">🔥 1</span>
                  <span className="bg-orange-500/20 text-orange-400 px-1 py-0.5 rounded text-xs">~ 1</span>
                </div>
              </div>
              <div className="p-2 space-y-2">
                <div className="bg-[#0f1729] border border-gray-800 rounded-lg p-2">
                  <div className="text-white text-xs font-medium mb-1">Gabriel Thomas</div>
                  <div className="text-gray-500 text-xs mb-1">Qualificado por IA</div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-red-400">🔥 30%</span>
                    <div className="w-3 h-3 flex items-center justify-center bg-green-500 rounded-full">
                      <i className="ri-check-line text-white text-xs"></i>
                    </div>
                  </div>
                </div>
                <div className="bg-[#0f1729] border border-gray-800 rounded-lg p-2">
                  <div className="text-white text-xs font-medium mb-1">Alice Torres</div>
                  <div className="text-gray-500 text-xs mb-1">Qualificado por IA</div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-orange-400">~ 65%</span>
                    <div className="w-3 h-3 flex items-center justify-center bg-green-500 rounded-full">
                      <i className="ri-check-line text-white text-xs"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Column 2: Contato Inicial */}
            <div className="w-48 bg-[#1a2332] rounded-lg border border-gray-800">
              <div className="p-2 border-b border-gray-800">
                <div className="flex items-center gap-1 mb-1">
                  <i className="ri-chat-3-fill text-blue-400 text-xs"></i>
                  <span className="text-white font-semibold text-xs">Contato Inicial</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-blue-400">2</span>
                  <span className="text-green-400">R$ 5.5k</span>
                </div>
              </div>
              <div className="p-2 space-y-2">
                <div className="bg-[#0f1729] border border-gray-800 rounded-lg p-2">
                  <div className="text-white text-xs font-medium mb-1">Daniel Smith</div>
                  <div className="text-gray-500 text-xs mb-1">Aguardando</div>
                  <div className="text-green-400 font-semibold text-xs mb-1">R$ 4.990</div>
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <i className="ri-phone-line text-xs"></i>
                    <span className="text-xs">11999990003</span>
                  </div>
                </div>
                <div className="bg-[#0f1729] border border-gray-800 rounded-lg p-2">
                  <div className="text-white text-xs font-medium mb-1">Clara Franco</div>
                  <div className="text-gray-500 text-xs mb-1">Respondeu</div>
                  <div className="text-green-400 font-semibold text-xs">R$ 499</div>
                </div>
              </div>
            </div>

            {/* Column 3: Negociação */}
            <div className="w-48 bg-[#1a2332] rounded-lg border border-gray-800">
              <div className="p-2 border-b border-gray-800">
                <div className="flex items-center gap-1 mb-1">
                  <i className="ri-shake-hands-fill text-yellow-400 text-xs"></i>
                  <span className="text-white font-semibold text-xs">Negociação</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-blue-400">3</span>
                  <span className="text-green-400">R$ 6.6k</span>
                </div>
              </div>
              <div className="p-2 space-y-2">
                <div className="bg-[#0f1729] border border-gray-800 rounded-lg p-2">
                  <div className="text-white text-xs font-medium mb-1">Sophia Lima</div>
                  <div className="text-gray-500 text-xs mb-1">Sem resposta</div>
                  <div className="text-green-400 font-semibold text-xs">R$ 1.990</div>
                </div>
                <div className="bg-[#0f1729] border border-gray-800 rounded-lg p-2">
                  <div className="text-white text-xs font-medium mb-1">Julia Mendes</div>
                  <div className="text-gray-500 text-xs mb-1">Em análise</div>
                  <div className="text-green-400 font-semibold text-xs">R$ 2.390</div>
                </div>
              </div>
            </div>

            {/* Column 4: Fechamento */}
            <div className="w-48 bg-[#1a2332] rounded-lg border border-gray-800">
              <div className="p-2 border-b border-gray-800">
                <div className="flex items-center gap-1 mb-1">
                  <i className="ri-checkbox-circle-fill text-green-400 text-xs"></i>
                  <span className="text-white font-semibold text-xs">Fechamento</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-blue-400">3</span>
                  <span className="text-green-400">R$ 4.6k</span>
                </div>
              </div>
              <div className="p-2 space-y-2">
                <div className="bg-[#0f1729] border border-gray-800 rounded-lg p-2">
                  <div className="text-white text-xs font-medium mb-1">Chris Diaz</div>
                  <div className="text-gray-500 text-xs mb-1">Resolvido</div>
                  <div className="text-green-400 font-semibold text-xs">R$ 100</div>
                </div>
                <div className="bg-[#0f1729] border border-gray-800 rounded-lg p-2">
                  <div className="text-white text-xs font-medium mb-1">Victor Santos</div>
                  <div className="text-gray-500 text-xs mb-1">Resolvido</div>
                  <div className="text-green-400 font-semibold text-xs">R$ 3.490</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MultiInboxMockup({ color }: { color: string }) {
  const { t } = useTranslation();
  const [selectedChat, setSelectedChat] = useState(0);

  const conversations = [
    {
      id: 0,
      name: 'Roberto de Souza',
      phone: '+5519981814895',
      avatar: '/assets/media/testimonial-roberto.jpg',
      lastMessage: 'Perfeito! Obrigado pela ajuda.',
      time: '14:40',
      unread: 0,
      status: 'online',
      messages: [
        { type: 'user', text: 'Olá! Gostaria de agendar uma consulta com cardiologista.', time: '14:35' },
        { type: 'bot', text: 'Olá Roberto! Claro, vou te ajudar. Temos disponibilidade com Dr. Carlos Silva. Qual dia você prefere?', time: '14:36' },
        { type: 'user', text: 'Pode ser na próxima terça-feira?', time: '14:37' },
        { type: 'bot', text: 'Terça-feira (16/05) temos os seguintes horários: 09:00, 10:30, 14:00 e 16:30. Qual prefere?', time: '14:38' },
        { type: 'user', text: 'Às 14:00 está ótimo.', time: '14:39' },
        { type: 'bot', text: 'Agendado! Consulta com Dr. Carlos Silva (Cardiologia) em 16/05 às 14:00. Você receberá um lembrete 1 dia antes. Precisa de algo mais?', time: '14:39' },
        { type: 'user', text: 'Perfeito! Obrigado pela ajuda.', time: '14:40' }
      ]
    },
    {
      id: 1,
      name: 'Carlos Ferreira',
      phone: '+5511987654321',
      avatar: '/assets/media/testimonial-carlos.jpg',
      lastMessage: 'Sim, por favor!',
      time: '14:34',
      unread: 0,
      status: 'online',
      messages: [
        { type: 'user', text: 'Bom dia! Preciso dos resultados do meu exame de sangue.', time: '14:30' },
        { type: 'bot', text: 'Bom dia, Carlos! Vou verificar para você. Qual o seu CPF?', time: '14:31' },
        { type: 'user', text: '123.456.789-00', time: '14:31' },
        { type: 'bot', text: 'Encontrei! Seus exames já estão prontos. Posso enviar por e-mail ou WhatsApp?', time: '14:32' },
        { type: 'user', text: 'Pode enviar por e-mail, por favor.', time: '14:33' },
        { type: 'bot', text: 'Enviado para carlos.ferreira@email.com! Gostaria de agendar uma consulta de retorno para avaliar os resultados?', time: '14:33' },
        { type: 'user', text: 'Sim, por favor!', time: '14:34' }
      ]
    },
    {
      id: 2,
      name: 'Lívia Braga',
      phone: '+5511976543210',
      avatar: '/assets/media/testimonial-livia.jpg',
      lastMessage: 'Obrigada!',
      time: '14:28',
      unread: 0,
      status: 'offline',
      messages: [
        { type: 'user', text: 'Oi! Vocês atendem convênio Unimed?', time: '14:25' },
        { type: 'bot', text: 'Olá Lívia! Sim, atendemos Unimed e diversos outros convênios. Qual especialidade você precisa?', time: '14:26' },
        { type: 'user', text: 'Preciso de um dermatologista.', time: '14:26' },
        { type: 'bot', text: 'Temos a Dra. Ana Paula disponível. Ela atende terças e quintas. Gostaria de agendar?', time: '14:27' },
        { type: 'user', text: 'Sim! Quinta-feira que vem pode ser?', time: '14:27' },
        { type: 'bot', text: 'Perfeito! Quinta (18/05) às 15:00 com Dra. Ana Paula. Traga sua carteirinha do convênio. Confirmado!', time: '14:28' },
        { type: 'user', text: 'Obrigada!', time: '14:28' }
      ]
    },
    {
      id: 3,
      name: 'Bruna Tomazelli',
      phone: '+5511965432109',
      avatar: '/assets/media/testimonial-bruna.jpg',
      lastMessage: 'Vou levar sim!',
      time: '14:18',
      unread: 0,
      status: 'offline',
      messages: [
        { type: 'user', text: 'Olá! Minha consulta é amanhã. Preciso levar algum exame?', time: '14:15' },
        { type: 'bot', text: 'Oi Bruna! Deixa eu verificar... Sua consulta é com Dr. Pedro (Ortopedista) às 10:00, correto?', time: '14:16' },
        { type: 'user', text: 'Isso mesmo!', time: '14:16' },
        { type: 'bot', text: 'O doutor solicitou que você traga o raio-X do joelho, se já tiver feito. Caso não tenha, ele pode solicitar na consulta.', time: '14:17' },
        { type: 'user', text: 'Já fiz! Vou levar sim!', time: '14:18' }
      ]
    },
    {
      id: 4,
      name: 'Mariana Costa',
      phone: '+5511954321098',
      avatar: '/assets/media/testimonial-mariana.jpg',
      lastMessage: 'Pode ser!',
      time: '14:05',
      unread: 0,
      status: 'offline',
      messages: [
        { type: 'user', text: 'Preciso remarcar minha consulta de amanhã.', time: '14:00' },
        { type: 'bot', text: 'Sem problemas, Mariana! Sua consulta está marcada para 15/05 às 09:00 com Dra. Juliana. Para quando gostaria de remarcar?', time: '14:01' },
        { type: 'user', text: 'Tem algum horário na semana que vem?', time: '14:02' },
        { type: 'bot', text: 'Sim! Temos disponibilidade: Segunda (22/05) às 14:00, Quarta (24/05) às 10:00 ou Sexta (26/05) às 16:00.', time: '14:03' },
        { type: 'user', text: 'Quarta às 10:00 funciona bem pra mim.', time: '14:04' },
        { type: 'bot', text: 'Remarcado! Nova data: 24/05 às 10:00 com Dra. Juliana. Confirma?', time: '14:04' },
        { type: 'user', text: 'Pode ser!', time: '14:05' }
      ]
    },
    {
      id: 5,
      name: 'Pedro Alves',
      phone: '+5511943210987',
      avatar: '/assets/media/testimonial-pedro.jpg',
      lastMessage: 'Entendi, obrigado!',
      time: '13:52',
      unread: 0,
      status: 'offline',
      messages: [
        { type: 'user', text: 'Quanto custa uma consulta particular com clínico geral?', time: '13:48' },
        { type: 'bot', text: 'Olá Pedro! A consulta particular com clínico geral custa R$ 180,00. Temos horários disponíveis ainda esta semana!', time: '13:49' },
        { type: 'user', text: 'E aceita cartão?', time: '13:50' },
        { type: 'bot', text: 'Sim! Aceitamos cartão de crédito, débito e PIX. Gostaria de agendar?', time: '13:50' },
        { type: 'user', text: 'Não agora, obrigado. Só queria saber o valor.', time: '13:51' },
        { type: 'bot', text: 'Sem problemas! Quando quiser agendar, é só chamar. Estamos aqui 24h! 😊', time: '13:52' },
        { type: 'user', text: 'Entendi, obrigado!', time: '13:52' }
      ]
    }
  ];

  return (
    <div className="relative group cursor-pointer">
      <div className="absolute inset-0 bg-gradient-to-br from-pink-600 to-pink-500 rounded-2xl blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-300"></div>
      <div className="relative bg-[#1c2632] backdrop-blur-sm border border-gray-800 rounded-2xl overflow-hidden shadow-2xl">
        {/* Main Container */}
        <div className="flex flex-col lg:flex-row h-auto lg:h-[500px]">
          {/* Left Sidebar - Conversations List */}
          <div className="w-full lg:w-80 bg-[#0f1729] border-b lg:border-b-0 lg:border-r border-gray-800 flex flex-col max-h-[300px] lg:max-h-none">
            {/* Header */}
            <div className="bg-[#1a2332] px-4 py-3 border-b border-gray-800">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                    <i className="ri-hospital-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm">Vitae Clínica</h3>
                    <p className="text-gray-400 text-xs">Painel do Agente</p>
                  </div>
                </div>
                <button className="w-8 h-8 flex items-center justify-center bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors">
                  <i className="ri-add-line text-white"></i>
                </button>
              </div>
              
              {/* Search Bar */}
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm"></i>
                <input 
                  type="text" 
                  placeholder="Buscar por nome, número..."
                  className="w-full bg-[#0f1729] border border-gray-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-green-500"
                />
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="px-4 py-2 bg-[#1a2332] border-b border-gray-800 flex items-center gap-2 overflow-x-auto">
              <button className="px-3 py-1 bg-green-500 text-white text-xs rounded-full font-medium whitespace-nowrap">
                <i className="ri-inbox-fill mr-1"></i>
                Todos (6)
              </button>
              <button className="px-3 py-1 bg-gray-800 text-gray-400 text-xs rounded-full font-medium whitespace-nowrap hover:bg-gray-700">
                <i className="ri-time-line mr-1"></i>
                Pendentes
              </button>
              <button className="px-3 py-1 bg-gray-800 text-gray-400 text-xs rounded-full font-medium whitespace-nowrap hover:bg-gray-700">
                <i className="ri-user-line mr-1"></i>
                Meus
              </button>
            </div>

            {/* Conversations List */}
            <div className="flex-1 overflow-y-auto">
              {conversations.map((conv) => (
                <div
                  key={conv.id}
                  onClick={() => setSelectedChat(conv.id)}
                  className={`px-4 py-3 border-b border-gray-800 cursor-pointer transition-colors ${
                    selectedChat === conv.id ? 'bg-[#1a2332]' : 'hover:bg-[#151e2b]'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <img 
                      src={conv.avatar} 
                      alt={conv.name}
                      className="w-10 h-10 rounded-full object-cover object-top flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-white font-medium text-sm truncate">{conv.name}</h4>
                        <span className="text-gray-500 text-xs flex-shrink-0 ml-2">{conv.time}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-gray-400 text-xs truncate">{conv.lastMessage}</p>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className={`w-2 h-2 rounded-full ${conv.status === 'online' ? 'bg-green-500' : 'bg-gray-600'}`}></div>
                        <span className="text-gray-500 text-xs">{conv.phone}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Side - Chat Area */}
          <div className="flex-1 flex flex-col bg-[#0a0e14] min-h-[400px] lg:min-h-0">
            {/* Chat Header */}
            <div className="bg-[#1a2332] px-4 py-3 border-b border-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img 
                    src={conversations[selectedChat].avatar} 
                    alt={conversations[selectedChat].name}
                    className="w-10 h-10 rounded-full object-cover object-top"
                  />
                  <div>
                    <h3 className="text-white font-semibold text-sm">{conversations[selectedChat].name}</h3>
                    <p className="text-gray-400 text-xs">{conversations[selectedChat].phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button className="w-8 h-8 flex items-center justify-center bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors">
                    <i className="ri-search-line text-gray-400"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors">
                    <i className="ri-more-2-fill text-gray-400"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {conversations[selectedChat].messages.length > 0 ? (
                conversations[selectedChat].messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.type === 'bot' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] lg:max-w-md px-4 py-2 rounded-lg ${
                        msg.type === 'bot'
                          ? 'bg-[#4158D0] text-white rounded-tr-sm'
                          : 'bg-[#1a2332] text-white rounded-tl-sm'
                      }`}
                    >
                      <p className="text-sm leading-relaxed">{msg.text}</p>
                      <span className="text-xs text-gray-400 mt-1 block text-right">{msg.time}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <i className="ri-chat-3-line text-gray-600 text-5xl mb-3"></i>
                    <p className="text-gray-500 text-sm">{t('solution.inbox.noConversation')}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Input Area */}
            <div className="bg-[#1a2332] px-4 py-3 border-t border-gray-800">
              <div className="flex items-center gap-2 lg:gap-3">
                <button className="w-9 h-9 flex items-center justify-center bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0">
                  <i className="ri-attachment-2 text-gray-400"></i>
                </button>
                <div className="flex-1 bg-[#0f1729] rounded-lg px-3 lg:px-4 py-2 flex items-center min-w-0">
                  <input 
                    type="text" 
                    placeholder={t('solution.inbox.inputPlaceholder')}
                    className="flex-1 bg-transparent text-white text-sm placeholder-gray-500 focus:outline-none min-w-0"
                  />
                  <i className="ri-emotion-happy-line text-gray-400 text-lg cursor-pointer flex-shrink-0"></i>
                </div>
                <button className="w-9 h-9 flex items-center justify-center bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0">
                  <i className="ri-send-plane-fill text-gray-400"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0">
                  <i className="ri-mic-fill text-gray-400"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
